import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Input,
  Button,
  TopProgress,
  CheckBox,
  InfoBlock,
} from '../../../../../components'
import {
  Container,
  CreateContainer,
} from './styles'
import * as yup from 'yup';
import { getIn, useFormik } from 'formik';
import { useUi } from '../../../../../context/ui.context'
import {openStackService} from '../../../../../api'

const regionValidationSchema = yup.object().shape({
  id: yup.string().required('Required field'),
  is_default: yup.bool(),
  is_lbaas_default: yup.bool(),
  description: yup.string().nullable(),
  parent_region_id: yup.string().nullable(),
  extra: yup.string().nullable(),
  enabled: yup.bool(),
  can_process_metrics: yup.bool(),
  disable_for_enduser: yup.bool(),
  enable_volumes: yup.bool(),
  enable_snapshots: yup.bool(),
  enable_networks: yup.bool(),
  enable_routers: yup.bool(),
  enable_instances: yup.bool(),
  floating_ip_on_instance_create: yup.bool(),
})

const RegionSettings: FC = () => {
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [settings, setSettings] = useState<any[]>([])

  const onSubmit = async (values:{ regions: any[] }) => {
    try {
      await openStackService.updateRegionSettings(values.regions)
      viewAlert({
        severity: 'success',
        message: t('CHANGES_SAVED'),
      })
    } catch (e:any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t('ERROR'),
      })
    }
  }

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      regions: yup.array().of(regionValidationSchema),
    }),
    initialValues: {
      regions: settings,
    },
    onSubmit,
  })
  const getInputProps = (index: number, field: string) => {
    const fieldName = `regions[${index}].${field}`
    return {
      fieldName,
      value: getIn(values, fieldName) ?? '',
      manualErr: getIn(errors, fieldName),
      manualTouched: getIn(touched, fieldName),
      handleChange,
      handleBlur,
    }
  }

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await openStackService.getRegionSettings()
        setSettings(res.data.objects)
      } catch (e:any) {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || t('ERROR'),
        })
      } finally {
        setPageLoading(false)
      }
    }
    getData()
  }, [t, viewAlert])

  if (pageLoading) return <TopProgress loading={pageLoading}/>

  return (
    <CreateContainer>
      <Container>
        {values.regions.map((region:any, index:number) => (
          <InfoBlock
            key={index}
            data={region}
            title={region.id || `Region ${index + 1}`}
            dropped
            customDropped={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px', marginBottom: '20px' }}>
                <Input
                  required
                  title={t("REGION_ID")}
                  placeholder=''
                  {...getInputProps(index, 'id')}
                />
                <CheckBox
                  value={region.is_default}
                  onChange={() => setFieldValue(`regions[${index}].is_default`, !region.is_default)}
                  title={t("IS_DEFAULT")}
                />
                <CheckBox
                  value={region.is_lbaas_default}
                  onChange={() => setFieldValue(`regions[${index}].is_lbaas_default`, !region.is_lbaas_default)}
                  title={t("IS_LBAAS_DEFAULT")}
                />
                <Input
                  title={t("DESCRIPTION")}
                  placeholder=''
                  {...getInputProps(index, 'description')}
                />
                <Input
                  title={t("PARENT_REGION_ID")}
                  placeholder=''
                  {...getInputProps(index, 'parent_region_id')}
                />
                <Input
                  title={t("EXTRA")}
                  placeholder=''
                  {...getInputProps(index, 'extra')}
                />
                <CheckBox
                  value={region.enabled}
                  onChange={() => setFieldValue(`regions[${index}].enabled`, !region.enabled)}
                  title={t("ENABLED")}
                />
                <CheckBox
                  value={region.can_process_metrics}
                  onChange={() => setFieldValue(`regions[${index}].can_process_metrics`, !region.can_process_metrics)}
                  title={t("CAN_PROCESS_METRICS")}
                />
                <CheckBox
                  value={region.disable_for_enduser}
                  onChange={() => setFieldValue(`regions[${index}].disable_for_enduser`, !region.disable_for_enduser)}
                  title={t("DISABLE_FOR_ENDUSER")}
                />
                <CheckBox
                  value={region.enable_volumes}
                  onChange={() => setFieldValue(`regions[${index}].enable_volumes`, !region.enable_volumes)}
                  title={t("ENABLE_VOLUMES")}
                />
                <CheckBox
                  value={region.enable_snapshots}
                  onChange={() => setFieldValue(`regions[${index}].enable_snapshots`, !region.enable_snapshots)}
                  title={t("ENABLE_SNAPSHOTS")}
                />
                <CheckBox
                  value={region.enable_networks}
                  onChange={() => setFieldValue(`regions[${index}].enable_networks`, !region.enable_networks)}
                  title={t("ENABLE_NETWORKS")}
                />
                <CheckBox
                  value={region.enable_routers}
                  onChange={() => setFieldValue(`regions[${index}].enable_routers`, !region.enable_routers)}
                  title={t("ENABLE_ROUTERS")}
                />
                <CheckBox
                  value={region.enable_instances}
                  onChange={() => setFieldValue(`regions[${index}].enable_instances`, !region.enable_instances)}
                  title={t("ENABLE_INSTANCES")}
                />
                <CheckBox
                  value={region.floating_ip_on_instance_create}
                  onChange={() => setFieldValue(`regions[${index}].floating_ip_on_instance_create`, !region.floating_ip_on_instance_create)}
                  title={t("FLOATING_IP_ON_INSTANCE_CREATE")}
                />
              </div>
            }
          />
        ))}
        <div style={{display:'flex', marginTop:'0px'}}>

          <Button
            variant="primary"
            size="display"
            customStyles={{marginRight:"10px"}}
            onClick={() => handleSubmit()}
            title={t("SAVE")}
            loading={isSubmitting}
            disabled={values.regions.length === 0}
          />
        </div>
      </Container>
    </CreateContainer>
  )
}

export default RegionSettings
